import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  Autocomplete,
  Chip
} from '@mui/material';

const SELECT_ALL = '__SELECT_ALL__';

const CompareAnalyteSettingsPanel = ({ 
  classes,
  compareAnalyteSettings, 
  onChange, 
  onSubmit, 
  analyteOptions = [],
  programOptions = []
}) => {
  const fullProgramOptions = [SELECT_ALL, ...programOptions];
  
  // Track focus state for Programs autocomplete
  const [isProgramsFocused, setIsProgramsFocused] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState(5);
  const programsInputRef = useRef(null);

  const handleProgramsChange = (event, newValue) => {
    if (newValue.includes(SELECT_ALL)) {
      // Select all programs (including 'ALL_PROGRAMS' option)
      onChange({ ...compareAnalyteSettings, selectPrograms: programOptions });
    } else {
      onChange({ ...compareAnalyteSettings, selectPrograms: newValue });
    }
  };

  const getLabel = (option) => {
    if (option === SELECT_ALL) return 'Select All';
    if (option === 'ALL_PROGRAMS') return 'All Programs';
    return option;
  };

  // Calculate visible tags when not focused
  useEffect(() => {
    if (isProgramsFocused || !compareAnalyteSettings.selectPrograms || compareAnalyteSettings.selectPrograms.length === 0) {
      return;
    }

    const calculateVisibleTags = () => {
      if (!programsInputRef.current) return;

      const inputElement = programsInputRef.current.querySelector('.MuiInputBase-root');
      if (!inputElement) return;

      const containerWidth = inputElement.offsetWidth;
      const reservedSpace = 100;
      const availableWidth = containerWidth - reservedSpace;
      
      const values = compareAnalyteSettings.selectPrograms || [];
      let totalWidth = 0;
      let count = 0;
      
      const chipGap = 4;
      const avgCharWidth = 8.5;
      const chipBasePadding = 32;
      const moreChipWidth = 50;

      for (let i = 0; i < values.length; i++) {
        const label = getLabel(values[i]);
        const chipWidth = (label.length * avgCharWidth) + chipBasePadding + chipGap;
        
        const needsMoreChip = i < values.length - 1;
        const requiredWidth = totalWidth + chipWidth + (needsMoreChip ? moreChipWidth : 0);
        
        if (requiredWidth > availableWidth && count > 0) {
          break;
        }
        
        totalWidth += chipWidth;
        count++;
      }

      setVisibleTagCount(Math.max(1, count));
    };

    const frame = requestAnimationFrame(() => {
      calculateVisibleTags();
    });

    return () => cancelAnimationFrame(frame);
  }, [compareAnalyteSettings.selectPrograms, isProgramsFocused]);

  return (
    <Grid container spacing={2} mb={2}>
      <Box component={Grid} item xs={12} sm={5}>
        <FormControl fullWidth>
          <Autocomplete
            size="small"
            className={classes.muiInputs}
            variant="outlined"
            id="selectAnalyte"
            options={analyteOptions}
            value={compareAnalyteSettings.selectAnalyte || null}
            onChange={(event, newValue) =>
              onChange({ ...compareAnalyteSettings, selectAnalyte: newValue })
            }
            getOptionLabel={(option) => option?.desc || ''}
            isOptionEqualToValue={(option, value) => option?.analyteCode === value?.analyteCode}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Analyte"
              />
            )}
          />
        </FormControl>
      </Box>

      <Box component={Grid} item xs={12} sm={5}>
        <FormControl fullWidth ref={programsInputRef}>
          <Autocomplete
            size="small"
            className={classes.muiInputs}
            variant="outlined"
            multiple
            id="selectPrograms"
            options={fullProgramOptions}
            value={compareAnalyteSettings.selectPrograms || []}
            onChange={handleProgramsChange}
            onFocus={() => setIsProgramsFocused(true)}
            onBlur={() => setIsProgramsFocused(false)}
            getOptionLabel={getLabel}
            renderTags={(value, getTagProps) => {
              // When focused, show all chips (default behavior)
              if (isProgramsFocused) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={getLabel(option)}
                    size="small"
                  />
                ));
              }
              
              // When not focused, limit to one line
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 0.5,
                    overflow: 'hidden',
                    width: '100%',
                    alignItems: 'center'
                  }}
                >
                  {value.slice(0, visibleTagCount).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={getLabel(option)}
                      size="small"
                    />
                  ))}
                  {value.length > visibleTagCount && (
                    <Chip
                      size="small"
                      label={`+${value.length - visibleTagCount}`}
                      sx={{ cursor: 'default' }}
                    />
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Programs to Compare"
                sx={{
                  '& .MuiInputBase-root': {
                    alignItems: 'center',
                    ...((!isProgramsFocused && compareAnalyteSettings.selectPrograms?.length > 0) && {
                      height: '40px',
                      minHeight: '40px',
                      maxHeight: '40px'
                    })
                  },
                  '& .MuiInputBase-root .MuiAutocomplete-input': {
                    minWidth: isProgramsFocused ? '30px' : '0px'
                  }
                }}
              />
            )}
            filterSelectedOptions
          />
        </FormControl>
      </Box>

      <Box component={Grid} item xs={12} sm={2}>
        <Button
          fullWidth
          variant="contained"
          onClick={onSubmit}
          disabled={!compareAnalyteSettings.selectAnalyte || !compareAnalyteSettings.selectPrograms?.length}
        >
          Submit
        </Button>
      </Box>
    </Grid>
  );
};

export default CompareAnalyteSettingsPanel;
